import { ref, onMounted, onUnmounted, Ref, computed, unref } from "vue";
import {
  CompositeHeadApiModel,
  CompositeHeadNodeApiModel,
  LeaderTypesApiModel,
} from "../../../api-models";
import { HeadNode, NodeType } from "./nodes-and-links-models";

type LinkInputType = {
  source: string;
  target: string;
  distance: {
    solo: number | null;
    viewpoints: number | null;
  };
};
export interface NodeAndLinksData {
  nodes: HeadNode[];
  links: LinkInputType[];
}

function getNodeType(node: CompositeHeadNodeApiModel): NodeType {
  if (!node.children?.length) return "Individual";
  return node.children.some((n) => n.children?.length) ? "Aggregate" : "Team";
}

function calculateDistance(
  a: Record<LeaderTypesApiModel, number>,
  b: Record<LeaderTypesApiModel, number>,
) {
  if (!a || !b) return null;

  const distanceSquared = Object.keys(a).reduce((total: number, key: string) => {
    return total + (a[key] - b[key]) ** 2;
  }, 0);
  return Math.sqrt(distanceSquared);
}

export default function useNodesAndLinksCurrentData(
  model: CompositeHeadApiModel,
  currentParentNode: Ref<CompositeHeadNodeApiModel | null>,
): { data: Ref<NodeAndLinksData> } {
  const data = computed(() => {
    if (!currentParentNode.value) {
      // Root node
      const { rootNode } = model;
      return {
        nodes: [
          {
            id: rootNode.id,
            name: rootNode.title,
            data: rootNode.data,
            type: getNodeType(rootNode),
          },
        ] as HeadNode[],
        links: [],
      };
    }
    const nodes =
      currentParentNode.value.children.map((node) => {
        return {
          id: node.id,
          name: node.title,
          data: node.data,
          type: getNodeType(node),
        };
      }) || [];
    const links: any[] = [];
    const linkMap: Record<string, boolean> = {};
    nodes.forEach((i) => {
      nodes.forEach((j) => {
        if (i !== j && !linkMap[i.id + j.id] && !linkMap[j.id + i.id]) {
          links.push({
            source: i.id,
            target: j.id,
            distance: {
              solo: calculateDistance(i.data.soloFits, j.data.soloFits),
              viewpoints: calculateDistance(i.data.viewpointsFits, j.data.viewpointsFits),
            },
          });
        }
      });
    });

    return {
      nodes,
      links,
    };
  });

  return { data };
}
